export type IdentitySourceBadge = {
  key: string;
  label: string;
  kind: string;
  icon: string;
  source: string;
};

export type IdentityFieldValue = string | number | boolean;

export type IdentityField = {
  key: string;
  label: string;
  value: IdentityFieldValue;
  source?: IdentitySourceBadge;
  description?: string;
  field_type?: "field" | "question" | "ai_label";
};

export type OutcomeSnapshot = {
  name?: string | null;
  label?: string | null;
  description?: string | null;
  set_at?: string | null;
  confidence?: number | null;
  reason?: string | null;
  source?: string | null;
  [key: string]: unknown;
};

export type ProfileAttributeEntry = {
  system_name: string;
  value: IdentityFieldValue;
  label?: string;
  agent_id?: string | null;
};

export type VisitorIdentityLead = {
  id?: string | null;
  organization_id?: string | null;
  account_id?: string | null;
  agent_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  email_verified?: boolean | null;
  identity_verified_at?: string | null;
  company?: string | null;
  job_title?: string | null;
  attributes?: Record<string, unknown>;
  integrations?: Record<string, unknown>;
  revenue?: number | null;
  external_id?: string | null;
  source?: string | null;
  latest_outcome?: OutcomeSnapshot | null;
  summary?: {
    text?: string | null;
    [key: string]: unknown;
  } | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type VisitorProfilePayload = {
  id?: string;
  visitor_id?: string;
  lead_id?: string | null;
  ip_address?: string | null;
  profile_attribute_entries?: ProfileAttributeEntry[];
  profile_attributes?: Record<string, unknown>;
  provider_profiles?: Record<string, unknown>;
  provider_last_seen_at?: Record<string, string | null>;
  person?: Record<string, unknown>;
  company?: Record<string, unknown>;
  sources?: {
    person?: Record<string, string>;
    company?: Record<string, string>;
  };
  query_params?: Record<string, unknown>;
  source_first?: string | null;
  source_latest?: string | null;
  first_seen_at?: string | null;
  last_seen_at?: string | null;
  referrer?: string | null;
  first_seen_url?: string | null;
  last_seen_url?: string | null;
  last_identified_at?: string | null;
  identity_verified_at?: string | null;
  latest_outcome?: OutcomeSnapshot | null;
  visitor_summary?: string | null;
};

export type BrowsingActivityPageView = {
  id?: string;
  page: string;
  url?: string;
  timestamp: string;
};

export type BrowsingActivityClickEvent = {
  id?: string;
  parent_window_url: string;
  timestamp: string;
  x?: number | null;
  y?: number | null;
  element?: string | null;
  element_name?: string | null;
  element_text?: string | null;
};

export type PageVisitCount = {
  url: string;
  path: string | null;
  count: number;
  last_visited_at: string | null;
};

export type BrowsingActivityPayload = {
  page_views: BrowsingActivityPageView[];
  click_events: BrowsingActivityClickEvent[];
  page_visit_counts: PageVisitCount[];
  current_page?: string | null;
  online?: boolean;
};

export type UnifiedVisitorIdentity = {
  visitor_id: string | null;
  account_id?: string | null;
  lead?: VisitorIdentityLead | null;
  visitor_profile?: VisitorProfilePayload | null;
  visitor_profiles?: VisitorProfilePayload[];
  summary: {
    status: "contact" | "identified" | "anonymous";
    display_name: string;
    subtitle?: string | null;
    avatar_text?: string | null;
    email?: string | null;
    linkedin_url?: string | null;
    badges: IdentitySourceBadge[];
    has_ip_address: boolean;
  };
  person: Record<string, unknown>;
  company: Record<string, unknown>;
  sources: {
    person?: Record<string, string>;
    company?: Record<string, string>;
  };
  providers: string[];
  visitor_summary?: string | null;
  has_identity_data: boolean;
  has_ip_address: boolean;
  lead_details: IdentityField[];
  person_fields: IdentityField[];
  company_fields: IdentityField[];
  collected_fields: IdentityField[];
  query_parameters: IdentityField[];
  location: IdentityField[];
  referrer: IdentityField[];
  browsing_activity?: BrowsingActivityPayload | null;
};

export type IdentityApiResponse = {
  visitor_id: string | null;
  visitor_profiles?: VisitorProfilePayload[];
  has_ip_address?: boolean;
  identity: UnifiedVisitorIdentity;
};

type IdentityDisplayPerson = {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  title?: string | null;
};

type IdentityDisplayCompany = {
  name?: string | null;
};

export type IdentityStatus = "contact" | "identified" | "anonymous";

type IdentityDisplayInput = {
  lead?: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    company?: string | null;
    job_title?: string | null;
  } | null;
  person?: IdentityDisplayPerson | Record<string, unknown> | null;
  company?: IdentityDisplayCompany | Record<string, unknown> | null;
};

export function getIdentityDisplayName(input: IdentityDisplayInput): string | null {
  const person = input.person as IdentityDisplayPerson | null | undefined;
  const company = input.company as IdentityDisplayCompany | null | undefined;
  const leadName = [input.lead?.first_name, input.lead?.last_name].filter(Boolean).join(" ").trim();
  if (leadName) return leadName;
  if (input.lead?.email) return input.lead.email;
  if (person?.full_name) return person.full_name;
  const personName = [person?.first_name, person?.last_name].filter(Boolean).join(" ").trim();
  if (personName) return personName;
  if (person?.email) return person.email;
  if (company?.name) return company.name;
  return null;
}

type IdentityStatusInput = {
  summaryStatus?: IdentityStatus | null;
  lead?: IdentityDisplayInput["lead"] | null;
  person?: IdentityDisplayInput["person"];
  company?: IdentityDisplayInput["company"];
};

export function resolveIdentityStatus(input: IdentityStatusInput): IdentityStatus {
  if (input.summaryStatus) return input.summaryStatus;
  if (input.lead) return "contact";
  return getIdentityDisplayName({ person: input.person, company: input.company })
    ? "identified"
    : "anonymous";
}

export function getIdentityVerificationTitle(input: {
  leadIdentityVerifiedAt?: string | null;
  sessionIdentityVerifiedAt?: string | null;
}): string | null {
  if (input.sessionIdentityVerifiedAt) return "Identity verified this session";
  if (input.leadIdentityVerifiedAt) return "Identity verified via signed SDK identify";
  return null;
}

export function getIdentitySubtitle(input: IdentityDisplayInput): string | null {
  const person = input.person as IdentityDisplayPerson | null | undefined;
  const company = input.company as IdentityDisplayCompany | null | undefined;
  if (input.lead?.job_title || input.lead?.company) {
    return [input.lead?.job_title, input.lead?.company].filter(Boolean).join(" at ");
  }
  if (person?.title || company?.name) {
    return [person?.title, company?.name].filter(Boolean).join(" at ");
  }
  return null;
}

export function getInitials(value?: string | null): string {
  if (!value) return "";
  return value
    .split(" ")
    .map((part) => part.trim()[0] || "")
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function getLeadDisplayLabel(lead?: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
} | null): string {
  const displayName = getIdentityDisplayName({ lead });
  return displayName || lead?.phone || "Anonymous";
}

export function getOutcomeDisplayLabel(outcome?: OutcomeSnapshot | null): string | null {
  const value = outcome?.label || outcome?.name;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function isUrlLike(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export function isEmailLike(value: string): boolean {
  return value.includes("@") && !value.includes(" ");
}

export function formatIdentityFieldValue(value: IdentityFieldValue): string {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
}
